<template>
  <v-alert v-model="isDisplayed"
           :border="noBorder ? null : 'start'"
           :closable="dismissible"
           :icon="noIcon ? false : icon"
           :type="type"
           :variant="variant"
           :class="[`alert--${type}`]"
           class="mb-0"
           v-bind="$attrs"
  >
    <!-- @slot for alert content -->
    <slot></slot>
    <template v-if="dismissible" #close>
      <AppButton class="ml-4"
                 color="black"
                 style="font-size: 16px;"
                 text
                 @click="closeAlert"
      >
        <app-material-icon name="close" />
      </AppButton>
    </template>
  </v-alert>
</template>

<script>
import AppButton from '../buttons/AppButton.vue'

const ALERT_TYPES = Object.freeze({
  info: 'info',
  warning: 'warning',
  error: 'error',
})
/**
 * Default Alert
 */
export default {
  name: 'Alert',
  components: {
    AppButton,
  },
  /*
  * All Vuetify props for Alert components are usable.
  * See doc : https://vuetifyjs.com/en/api/v-alert/#props
  */
  inheritAttrs: false,
  props: {
    /**
     * display state
     */
    value: {
      type: Boolean,
      default: undefined,
      validator: val => val === true || val === false || val === undefined,
    },
    /**
     * Designates a specific icon
     */
    icon: {
      type: [Boolean, String],
      default: 'info',
    },
    /**
     * Adds a close icon that can hide the alert
     */
    dismissible: {
      type: Boolean,
      default: true,
    },
    /**
     * Applies the defined color to text and a low opacity background of the same
     */
    text: {
      type: Boolean,
      default: false,
    },
    /**
     * Specify a success, info, warning or error alert. Uses the contextual color and has a pre-defined icon
     */
    type: {
      type: String,
      default: 'info',
      validator: value => {
        return value in ALERT_TYPES
      },
    },
    /**
     * Wether the left icon is displayed
     */
    noIcon: {
      type: Boolean,
      default: false,
    },
    /**
     * Wether the left border is displayed
     */
    noBorder: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      internalVisible: true,
    }
  },
  computed: {
    isDisplayed: {
      get () {
        return this.value !== undefined ? this.value : this.internalVisible
      },
      set (val) {
        if (this.value !== undefined) {
          this.$emit('input', val)
        } else {
          this.internalVisible = val
        }
      },
    },
    variant () {
      return this.text ? 'text': 'tonal'
    },
  },
  methods: {
    closeAlert () {
      this.isDisplayed = false
    },
  },
}
</script>

<style scoped lang="scss">
  :deep(.v-alert__content) {
    color: rgb(var(--v-theme-text-secondary));
  }

  :deep(.v-alert__prepend) {
    margin-inline-end: 8px;
  }

  :deep(.v-alert__underlay) {
    opacity: 0;
  }

  .alert--info {
    background-color: rgb(var(--v-theme-surface-info)) !important;

    :deep(.v-alert__border) {
      border-color: rgb(var(--v-theme-border-info-bold));
      opacity: 1;
    }
  }

  .alert--warning {
    background-color: rgb(var(--v-theme-surface-warning)) !important;

    :deep(.v-alert__border) {
      border-color: rgb(var(--v-theme-border-warning-bold));
      opacity: 1;
    }
  }

  .alert--error {
    background-color: rgb(var(--v-theme-surface-danger)) !important;

    :deep(.v-alert__border) {
      border-color: rgb(var(--v-theme-border-danger-bold));
      opacity: 1;
    }
  }
</style>
